// OP-3617 — Hoje (ESL1): a porta do módulo. O que espera decisão, agrupado por
// quem decide; cada linha leva à lista já filtrada pelo status que conta. Os
// números vêm do servidor já no escopo de praça de quem está logado.
import { FC, useMemo, useState } from 'react'
import styled from 'styled-components'
import { Link, Navigate } from 'react-router-dom'
import { useEstoqueHoje } from '../../../../hooks/estoque'
import { BD, SEVERIDADE, Button, Card, PageSubtitle, PageTitle, Spinner, StatusBadge } from '../../styles'
import { useCelular } from '../../useCelular'
import { fmtDate } from '../../statusUi'
import { plural } from '../formato'
import { GrupoHoje, JANELA_PADRAO_DIAS, LinhaHoje, gruposDoHoje, totalDoGrupo } from './regras'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'

const COR: Record<LinhaHoje['severidade'], { bg: string; color: string }> = {
  atencao: { bg: '#FEF3C7', color: '#92400E' },
  critico: { bg: SEVERIDADE.critico.bg, color: SEVERIDADE.critico.texto },
}

const Numero: FC<{ l: LinhaHoje }> = ({ l }) => {
  if (l.total <= 0) return <span style={{ fontSize: 15, fontWeight: 600, color: BD.grayText, minWidth: 36, textAlign: 'right' }}>0</span>
  const cor = COR[l.severidade]
  return <StatusBadge $bg={cor.bg} $color={cor.color} style={{ fontSize: 14, minWidth: 36, justifyContent: 'center' }}>{l.total}</StatusBadge>
}

const Linha: FC<{ l: LinhaHoje }> = ({ l }) => (
  <Link to={l.rota}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, minHeight: 46, padding: '8px 0', borderTop: `1px solid ${BD.grayBorder}`, textDecoration: 'none', color: l.total > 0 ? BD.black : BD.grayText, fontSize: 14 }}>
    <span>{l.rotulo}</span>
    <Numero l={l} />
  </Link>
)

const VerTodas = styled.button`
  width: 100%;
  min-height: 44px;
  border: 0;
  border-top: 1px solid ${BD.grayBorder};
  background: transparent;
  color: ${BD.grayText};
  font-family: inherit;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
`

/**
 * OP-3619 — no celular a lista inteira empurra os grupos para fora da tela:
 * as linhas zeradas ficam atrás de um toque, e o grupo já diz quantas são.
 * No desktop a tela continua como era, com as vinte linhas à vista.
 */
const Grupo: FC<{ g: GrupoHoje }> = ({ g }) => {
  const celular = useCelular()
  const [tudo, setTudo] = useState(false)
  const total = totalDoGrupo(g)
  const esperando = g.linhas.filter((l) => l.total > 0)
  const zeradas = celular ? g.linhas.length - esperando.length : 0
  const visiveis = celular && !tudo ? esperando : g.linhas
  return (
    <Card style={{ padding: '4px 16px 8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, paddingTop: 12 }}>
        <div style={{ fontWeight: 600 }}>{g.titulo}</div>
        <div style={{ fontSize: 12, color: BD.grayText }}>{total > 0 ? `${total} esperando` : 'Nada esperando.'}</div>
      </div>
      <div style={{ fontSize: 12, color: BD.grayText, marginBottom: 8 }}>{g.descricao}</div>
      {visiveis.map((l) => <Linha key={l.chave} l={l} />)}
      {zeradas > 0 && (
        <VerTodas type="button" onClick={() => setTudo((v) => !v)}>
          {tudo
            ? 'Esconder o que não espera decisão'
            : `Mostrar ${zeradas === 1 ? 'a outra linha' : `as outras ${zeradas} linhas`} (sem nada esperando)`}
        </VerTodas>
      )}
    </Card>
  )
}

export const EstoqueHoje: FC = () => {
  const { data, isLoading, isError, isUnauthorized, mutate } = useEstoqueHoje()
  const [atualizando, setAtualizando] = useState(false)
  const grupos = useMemo<GrupoHoje[]>(() => (data ? gruposDoHoje(data.contadores, data.janelaDias) : []), [data])

  if (isUnauthorized && !LOCAL_EXECUTION) return <Navigate to="/auth" replace />

  const atualizar = async () => {
    setAtualizando(true)
    try { await mutate() } finally { setAtualizando(false) }
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <PageTitle>Hoje</PageTitle>
          <PageSubtitle>O que espera decisão no estoque, agrupado por quem decide. Os números respeitam a sua praça.</PageSubtitle>
        </div>
        <Button $variant="secondary" disabled={atualizando || isLoading} onClick={atualizar}>{atualizando ? 'Atualizando…' : '↻ Atualizar'}</Button>
      </div>

      {isLoading && <Card><div style={{ textAlign: 'center', padding: 24 }}><Spinner /></div></Card>}
      {!isLoading && isError && (
        <Card><div style={{ padding: 24, color: BD.red }}>
          Não foi possível carregar o resumo de hoje. <Button $variant="secondary" onClick={() => mutate()}>Tentar de novo</Button>
        </div></Card>
      )}
      {!isLoading && !isError && data && (
        <>
          {Number(data.contadores.remessasAtrasadas) > 0 && (
            <Link to="/interno/estoque/remessas" style={{ textDecoration: 'none' }}>
              <Card style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, marginBottom: 16, borderColor: SEVERIDADE.critico.borda, background: SEVERIDADE.critico.bg }}>
                <div style={{ flexGrow: 1 }}>
                  <div style={{ fontWeight: 600, color: SEVERIDADE.critico.texto }}>
                    {plural(Number(data.contadores.remessasAtrasadas), 'remessa atrasada', 'remessas atrasadas')}
                  </div>
                  {Number(data.contadores.remessasVencendo) > 0 && (
                    <div style={{ fontSize: 12, color: BD.grayText }}>
                      e {Number(data.contadores.remessasVencendo)} {Number(data.contadores.remessasVencendo) === 1 ? 'vence' : 'vencem'} nos próximos {data.janelaDias || JANELA_PADRAO_DIAS} dias
                    </div>
                  )}
                </div>
                <span aria-hidden="true" style={{ color: SEVERIDADE.critico.texto, fontSize: 18 }}>›</span>
              </Card>
            </Link>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, alignItems: 'start' }}>
            {grupos.map((g) => <Grupo key={g.id} g={g} />)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 16, fontSize: 12, color: BD.grayText }}>
            <span>Referência: {fmtDate(data.hoje)}</span>
            <Link to="/interno/estoque/posicao" style={{ color: BD.grayText, display: 'inline-flex', alignItems: 'center', minHeight: 44, padding: '0 8px', margin: '-12px -8px' }}>Ver posição</Link>
          </div>
        </>
      )}
    </>
  )
}

export default EstoqueHoje
